import React, { useCallback } from 'react';
import type { RichContent } from '../../types';
import { isSafeUrl } from '../../utils/urlSafety';

interface ButtonsMessageProps {
  content: RichContent;
  onButtonClick?: (value: string) => void;
  trackAnalytics?: (action: string, data: Record<string, unknown>) => void;
}

const ButtonsMessage = React.memo(function ButtonsMessage({
  content,
  onButtonClick,
  trackAnalytics,
}: ButtonsMessageProps) {
  const handleButtonClick = useCallback(
    (button: {
      onClick?: () => void;
      value?: string;
      label: string;
      href?: string;
      target?: '_self' | '_blank';
    }) => {
      trackAnalytics?.('button_click', {
        label: button.label,
        value: button.value,
        hasHref: Boolean(button.href),
        target: button.target || null,
      });
      if (button.href && isSafeUrl(button.href) && typeof window !== 'undefined') {
        if (button.target === '_self') {
          try {
            window.open(button.href, '_self');
          } catch {
            try {
              window.location.assign(button.href);
            } catch {
              // Ignore if navigation is blocked.
            }
          }
        } else {
          window.open(button.href, '_blank', 'noopener,noreferrer');
        }
        if (button.value && onButtonClick) {
          onButtonClick(button.value);
        }
        return;
      }
      button.onClick?.();
      if (!button.onClick && onButtonClick) {
        onButtonClick(button.value || button.label);
      }
    },
    [onButtonClick, trackAnalytics]
  );

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {content.buttons?.map((button, idx) => (
        <button
          key={idx}
          onClick={() => handleButtonClick(button)}
          type="button"
          className={
            content.buttonStyle === 'pill'
              ? 'px-4 py-2 rounded-full border border-gray-200 bg-white text-gray-700 text-sm font-medium shadow-sm hover:bg-gray-100 focus:outline-2 focus:outline-blue-500'
              : 'px-3 py-1.5 text-xs rounded-lg border border-blue-600 text-blue-600 hover:bg-blue-50 focus:outline-2 focus:outline-blue-500'
          }
        >
          {button.label}
        </button>
      ))}
    </div>
  );
});

export default ButtonsMessage;
